import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "container", "errors", "submit"]
  static values = { existingCount: Number }

  connect() {
    this.files = [] // store selected files with unique IDs
    this.counter = 0 // unique ID counter
    this.maxFiles = 3
    this.pendingReads = 0
    this.activeUploads = 0
  }

  showPreviews(event) {
    this.clearErrors()

    const newFiles = Array.from(event.target.files)
    const totalAlready = this.existingCountValue + this.files.length

    if (totalAlready >= this.maxFiles) {
      this.addError(`You already uploaded the maximum of ${this.maxFiles} images.`)
      this.inputTarget.value = ""
      return
    }

    if (totalAlready + newFiles.length > this.maxFiles) {
      const allowed = this.maxFiles - totalAlready
      this.addError(`You can only add ${allowed} more image${allowed > 1 ? "s" : ""}.`)
      this.inputTarget.value = ""
      return
    }

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"]
    const validSize = 25 * 1024 * 1024 // 25MB
    const validFiles = []

    newFiles.forEach(file => {
      if (validTypes.includes(file.type)) {
        if (file.size > validSize) {
          this.addError(`"${file.name}" is too large. Each image must be less than 25MB.`)
          return
        }
        file._id = this.counter++
        validFiles.push(file)
      } else {
        this.addError(`"${file.name}" is not a valid image. Only PNG, JPG, JPEG, or WEBP allowed.`)
      }
    })

    this.files.push(...validFiles)

    this.renderPreviews()
    this.updateInputFiles()
  }

  removeFile(fileId) {
    this.files = this.files.filter(f => f._id !== fileId)
    const wrapper = this.containerTarget.querySelector(`[data-file-id="${fileId}"]`)
    if (wrapper) wrapper.remove()
    this.updateInputFiles()
  }

  renderPreviews() {
    this.containerTarget.innerHTML = ""
    this.pendingReads = this.files.length
    this.toggleSubmit()

    this.files.forEach(file => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative group mb-[24px]"
      wrapper.dataset.fileId = file._id

      const skeleton = document.createElement("div")
      skeleton.className = "rounded shadow w-full bg-gray-100 flex flex-col items-center justify-center gap-2 animate-pulse"
      skeleton.style.minHeight = "200px"

      const spinner = document.createElement("div")
      spinner.className = "w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"

      const label = document.createElement("p")
      label.className = "text-sm text-gray-500 mt-2"
      label.textContent = `Loading ${file.name}…`

      skeleton.appendChild(spinner)
      skeleton.appendChild(label)
      wrapper.appendChild(skeleton)
      this.containerTarget.appendChild(wrapper)

      const reader = new FileReader()

      reader.onload = e => {
        skeleton.remove()

        const img = document.createElement("img")
        img.src = e.target.result
        img.className =
          "rounded shadow object-cover w-full transition-transform duration-200"

        const removeBtn = document.createElement("button")
        removeBtn.type = "button"
        removeBtn.innerText = "✖"
        removeBtn.className =
          "absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded opacity-80"
        removeBtn.addEventListener("click", () => this.removeFile(file._id))

        wrapper.prepend(img)
        wrapper.appendChild(removeBtn)

        this.pendingReads--
        this.toggleSubmit()
      }

      reader.onerror = () => {
        skeleton.remove()
        const errorMsg = document.createElement("p")
        errorMsg.className = "text-red-600 text-sm py-4 text-center"
        errorMsg.textContent = `Failed to load ${file.name}`
        wrapper.appendChild(errorMsg)

        this.pendingReads--
        this.toggleSubmit()
      }

      reader.readAsDataURL(file)
    })
  }

  toggleSubmit() {
    if (!this.hasSubmitTarget) return
    const loading = this.pendingReads > 0
    const uploading = this.activeUploads > 0

    if (uploading) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.submitTarget.value = "Uploading images…"
    } else if (loading) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.submitTarget.value = "Loading images…"
    } else {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
      this.submitTarget.value = "Save Images"
    }
  }

  updateInputFiles() {
    const dataTransfer = new DataTransfer()
    this.files.forEach(file => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
  }

  addError(message) {
    const errorEl = document.createElement("p")
    errorEl.className = "text-red-600 text-sm mt-2"
    errorEl.textContent = message
    this.errorsTarget.appendChild(errorEl)
  }

  clearErrors() {
    this.errorsTarget.innerHTML = ""
  }

  uploadInitialize(event) {
    const { id, file } = event.detail

    const originalFile = this.files.find(f => f.name === file.name && f.size === file.size)

    if (originalFile) {
      const wrapper = this.containerTarget.querySelector(`[data-file-id="${originalFile._id}"]`)
      if (wrapper) {
        wrapper.dataset.directUploadId = id

        let progressContainer = wrapper.querySelector(".upload-progress-container")
        if (!progressContainer) {
          progressContainer = document.createElement("div")
          progressContainer.className = "upload-progress-container absolute bottom-0 left-0 right-0 h-2 bg-gray-200 rounded-b opacity-90 overflow-hidden"

          const progressBar = document.createElement("div")
          progressBar.className = "upload-progress-bar h-full bg-blue-600 w-0 transition-all duration-300"
          progressContainer.appendChild(progressBar)

          wrapper.appendChild(progressContainer)
        } else {
          progressContainer.classList.remove("hidden")
          const progressBar = progressContainer.querySelector(".upload-progress-bar")
          if (progressBar) progressBar.style.width = "0%"
        }
      }
    }
  }

  uploadStart(event) {
    this.activeUploads = (this.activeUploads || 0) + 1
    this.toggleSubmit()
  }

  uploadProgress(event) {
    const { id, progress } = event.detail
    const wrapper = this.containerTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (wrapper) {
      const progressBar = wrapper.querySelector('.upload-progress-bar')
      if (progressBar) progressBar.style.width = `${progress}%`
    }
  }

  uploadEnd(event) {
    const { id } = event.detail
    const wrapper = this.containerTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (wrapper) {
      const progressBar = wrapper.querySelector('.upload-progress-bar')
      if (progressBar) {
        progressBar.style.width = `100%`
        progressBar.classList.replace('bg-blue-600', 'bg-green-500')
      }
    }

    this.activeUploads = Math.max(0, (this.activeUploads || 0) - 1)
    this.toggleSubmit()
  }
}